
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FormContainer,
  FormHeader,
  FormField,
  LoadingBox,
  SubmitButton,
  ValidationError,
  WarningBox,
} from "./CreateGestanteForm.styles";
import CpfInputField from "../FormField/CpfInputField";
import ConfirmModal from "../Modals/ConfirmModal";
import api from "../../services/api";
import { formatValueErrorMessage } from "../../utils/errorMessage";
import { getUserProfile } from "../../utils/auth";
import ToastManager from '../../components/Toast/ToastManager';
import { useToast } from '../../hooks/useToast';
import { useGestanteFormConfig } from '../../hooks/useGestanteFormConfig';
import IdentidadeGeneroSection, {
  IDENTIDADE_INITIAL_STATE,
  montarPayloadIdentidade,
  podeApresentarIdentidade,
  validarIdentidade,
} from "./IdentidadeGeneroSection";

// US-102013: textos e código de erro espelham o backend (schemas.py). A grafia
// "CádÚnico" reproduz o critério de aceite da US literalmente
export const MSG_CONFIRMAR_SEM_CADUNICO =
  "O CPF não faz parte do CádÚnico, deseja confirmar inclusão da gestante mesmo assim?";
export const MSG_DATA_NASCIMENTO_OBRIGATORIA =
  "Informe a data de nascimento da gestante";
const ERRO_CPF_FORA_DO_CADUNICO = "CPF_FORA_DO_CADUNICO";

const FORM_INITIAL_STATE = {
  nome: "",
  cpf: "",
  cns: "",
  municipio_cod_ibge: "",
  data_nascimento: "",
};

const hojeISO = () => new Date().toISOString().slice(0, 10);

const CreateGestanteForm = () => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(FORM_INITIAL_STATE);
  const [identidade, setIdentidade] = useState(IDENTIDADE_INITIAL_STATE);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [municipios, setMunicipios] = useState([]);
  const [podeApresentarCpf, setPodeApresentarCpf] = useState(false);
  const [verificandoCpf, setVerificandoCpf] = useState(false);
  // null = indeterminado (CPF incompleto ou consulta que falhou); nesse estado
  // o fluxo de inclusão manual não é oferecido e o backend segue decidindo
  const [encontradoNoCadunico, setEncontradoNoCadunico] = useState(null);
  const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);
  const { toasts, removeToast, showSuccess, showError, showWarning } = useToast();
  const { gender_orientation_ui_enabled: identidadeFlag } = useGestanteFormConfig();

  // US-102013: só o administrador inclui gestante com CPF fora do CadÚnico
  const isAdmin = getUserProfile() === "Administrador";
  const inclusaoManual = isAdmin && encontradoNoCadunico === false;

  // Regra de idade (US-102684): a seção só aparece com idade >= 18 anos. Na
  // inclusão manual não há CadÚnico para consultar, então a idade vem da data
  // digitada — a mesma condição que o backend aplica sobre a data efetiva
  const identidadeHabilitada =
    Boolean(identidadeFlag) &&
    (inclusaoManual
      ? podeApresentarIdentidade(formValues.data_nascimento)
      : podeApresentarCpf);

  const handleIdentidadeChange = (name, value) => {
    setIdentidade((prev) => ({ ...prev, [name]: value }));
    // O erro pertence ao valor que acabou de mudar; mantê-lo faria a mensagem
    // sobreviver à própria causa. Trocar o select também limpa o erro do texto
    // livre, que só é exigido por "OUTRO" e cujo campo desmonta junto — sem
    // isso a mensagem volta a aparecer ao reabrir "Outro", já resolvida
    setValidationErrors((prev) => {
      if (!prev[name] && !prev[`${name}_other`]) return prev;
      const next = { ...prev };
      delete next[name];
      delete next[`${name}_other`];
      return next;
    });
  };

  const cpfDigits = (formValues.cpf || "").replace(/\D/g, "");

  useEffect(() => {
    if (cpfDigits.length !== 11) {
      setPodeApresentarCpf(false);
      setEncontradoNoCadunico(null);
      setVerificandoCpf(false);
      setIdentidade(IDENTIDADE_INITIAL_STATE);
      setFormValues((prev) =>
        prev.data_nascimento ? { ...prev, data_nascimento: "" } : prev
      );
      return;
    }
    let ativo = true;
    setVerificandoCpf(true);
    api
      .get(`/gestante/cadunico/${cpfDigits}/identidade-permitida`)
      .then((response) => {
        if (!ativo) return;
        setPodeApresentarCpf(Boolean(response?.data?.pode_apresentar));
        const encontrado = response?.data?.encontrado_no_cadunico;
        setEncontradoNoCadunico(
          typeof encontrado === "boolean" ? encontrado : null
        );
      })
      .catch(() => {
        if (!ativo) return;
        setPodeApresentarCpf(false);
        // falha na consulta não pode ser lida como "fora do CadÚnico": isso
        // abriria a inclusão manual indevidamente
        setEncontradoNoCadunico(null);
      })
      .finally(() => {
        if (ativo) setVerificandoCpf(false);
      });
    return () => {
      ativo = false;
    };
  }, [cpfDigits]);
  const handleChange = (e) => {
    const { name, value } = e.target;

    // validation max 15 characters in CNS
    if (name === 'cns') {
      const numbersOnly = value.replace(/\D/g, ''); // remove all non-digits
      if (numbersOnly.length <= 15) {
        setFormValues({ ...formValues, [name]: numbersOnly });
      }
      return;
    }

    setFormValues({ ...formValues, [name]: value });
  };

  // Buscar municípios do backend
  useEffect(() => {
    const fetchMunicipios = async () => {
      try {
        const page = 1;
        const size = 853;
        const response = await api.get("/municipios/list", {
          params: { page, size },
        });
        setMunicipios(response.data);
      } catch (error) {
        showError("Erro ao buscar municípios.");
        console.error("Erro ao buscar municípios:", error);
      }
    };

    fetchMunicipios();
  }, []);

  // Enviando requisição para o backend
  const enviarCadastro = async (confirmado) => {
    setIsSubmitting(true);

    const sanitizedCpf = formValues.cpf ? formValues.cpf.replace(/[.-]/g, "") : "";
    const sanitizedCns = formValues.cns.length === 0 ? null : formValues.cns;
    const { data_nascimento: dataNascimento, ...camposBase } = formValues;

    const sanitizedData = {
      ...camposBase,
      cpf: sanitizedCpf,
      municipio_cod_ibge: String(formValues.municipio_cod_ibge).padStart(7, '0'),
      cns: sanitizedCns,
      ...(identidadeHabilitada ? montarPayloadIdentidade(identidade) : {}),
      // A data digitada só acompanha a inclusão manual confirmada; com CPF no
      // CadÚnico a data é definida pelo backend
      ...(confirmado
        ? {
            data_nascimento: dataNascimento,
            confirmar_cadastro_sem_cadunico: true,
          }
        : {}),
    };

    try {
      await api.post("/gestante/create/", sanitizedData);
      setFormValues(FORM_INITIAL_STATE);
      setIdentidade(IDENTIDADE_INITIAL_STATE);
      setValidationErrors({});

      showSuccess("Gestante cadastrada com sucesso!");

      //adicionado timeout para esperar o toast aparecer antes de navegar
      setTimeout(()=>
        navigate(`/gestante/edit/${sanitizedCpf}`)
      , 1500);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 422 && Array.isArray(detail)) {
        const fieldErrors = {};
        detail.forEach((err) => {
          const fieldName = err.loc?.[1]; // loc: ['body', 'campo']
          const message = err.msg;
          if (fieldName) fieldErrors[fieldName] = message;
        });
        setValidationErrors(fieldErrors);
      } else if (detail?.error === ERRO_CPF_FORA_DO_CADUNICO) {
        // A pré-checagem do CPF não concluiu (rede/backend antigo) e o servidor
        // pediu a confirmação: converge para o mesmo estado de UI
        setEncontradoNoCadunico(false);
        showWarning(detail.message || MSG_CONFIRMAR_SEM_CADUNICO);
      } else {
        showError(
          typeof detail === "string" ? detail : "Erro ao enviar o formulário."
        );
        console.error("Erro ao enviar o formulário:", error);
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    // A data governa a regra de idade, então é validada antes da identidade
    if (inclusaoManual && !formValues.data_nascimento) {
      setValidationErrors({ data_nascimento: MSG_DATA_NASCIMENTO_OBRIGATORIA });
      return;
    }

    const identidadeErrors = identidadeHabilitada ? validarIdentidade(identidade) : {};
    if (Object.keys(identidadeErrors).length > 0) {
      setValidationErrors(identidadeErrors);
      return;
    }

    // US-102013 (cenários 1 e 2): nada é enviado antes da confirmação
    if (inclusaoManual) {
      setConfirmacaoAberta(true);
      return;
    }

    enviarCadastro(false);
  }

  return (
    <>
    <FormContainer>
      <FormHeader>Adicionar Gestante</FormHeader>
      <form onSubmit={handleSubmit}>
        <FormField>
          <CpfInputField
            value={formValues.cpf || ""}
            onChange={handleChange}
            error={validationErrors.cpf}
          />
        </FormField>

        {verificandoCpf && (
          <LoadingBox role="status">Verificando CPF...</LoadingBox>
        )}

        {inclusaoManual && !verificandoCpf && (
          <>
            <WarningBox role="alert">
              O CPF informado não foi localizado no CadÚnico. Informe a data de
              nascimento da gestante para prosseguir.
            </WarningBox>

            <FormField>
              <label htmlFor="data_nascimento">Data de Nascimento:</label>
              <input
                type="date"
                id="data_nascimento"
                name="data_nascimento"
                value={formValues.data_nascimento || ""}
                onChange={handleChange}
                max={hojeISO()}
                required
              />
              {validationErrors.data_nascimento && (
                <ValidationError>
                  {formatValueErrorMessage(validationErrors.data_nascimento)}
                </ValidationError>
              )}
            </FormField>
          </>
        )}

        <FormField>
          <label htmlFor="nome">Nome da Gestante:</label>
          <input
            type="text"
            id="nome"
            name="nome"
            value={formValues.nome || ""}
            onChange={handleChange}
            required
          />
          {validationErrors.nome && <ValidationError>{validationErrors.nome}</ValidationError>}
        </FormField>

        <FormField>
          <label htmlFor="cns">CNS da Gestante:</label>
          <input
            type="text"
            id="cns"
            name="cns"
            value={formValues.cns || ""}
            onChange={handleChange}
            maxLength={15}
            minLength={15}
            pattern="[0-9]{15}"
            placeholder="Digite os 15 dígitos do CNS"
          />
          {validationErrors.cns && <ValidationError>
            {formatValueErrorMessage(validationErrors.cns)}
          </ValidationError>}
        </FormField>

        <FormField>
          <label htmlFor="municipio_cod_ibge">Município:</label>
          <select
            id="municipio_cod_ibge"
            name="municipio_cod_ibge"
            value={formValues.municipio_cod_ibge || ""}
            onChange={handleChange}
          >
            <option value="">Selecione...</option>
            {municipios.length > 0 ? (
              municipios.map((municipio) => (
                <option key={municipio.cod_ibge} value={municipio.cod_ibge}>
                  {municipio.nome}
                </option>
              ))
            ) : (
              <option disabled>Carregando Municípios...</option>
            )}
          </select>
          {validationErrors.municipio_cod_ibge && <ValidationError>{validationErrors.municipio_cod_ibge}</ValidationError>}
        </FormField>

        {identidadeHabilitada && !verificandoCpf && (
          <IdentidadeGeneroSection
            values={identidade}
            onChange={handleIdentidadeChange}
            errors={validationErrors}
          />
        )}

        <SubmitButton type="submit" disabled={isSubmitting || verificandoCpf}>
          {isSubmitting ? "Enviando..." : "Enviar"}
        </SubmitButton>
      </form>


    </FormContainer>
     {/* US-102013: confirmação da inclusão de gestante fora do CadÚnico */}
          <ConfirmModal
            isOpen={confirmacaoAberta}
            onClose={() => setConfirmacaoAberta(false)}
            onConfirm={() => enviarCadastro(true)}
            message={MSG_CONFIRMAR_SEM_CADUNICO}
            confirmText="Confirmar"
            cancelText="Cancelar"
          />
     {/* Toast Manager para notificações */}
          <ToastManager toasts={toasts} removeToast={removeToast} />
    </>

  );
};

export default CreateGestanteForm;
